import axios from 'axios';
import { API_ROOT } from './apiClient';
import { AUTH_STORAGE_KEYS } from './tenantSession';

const authClient = axios.create({
  baseURL: `${API_ROOT}/auth/`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

authClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Espejo local de las preferencias ya conocidas.
//
// Cada listado espera la respuesta de `ui-preferences/` ANTES de pedir sus
// datos (necesita saber con qué filtros y orden pedirlos). Ese ida y vuelta
// costaba ~700 ms en cascada al abrir cualquier módulo: primero la preferencia,
// recién después las filas. Con el espejo local la pantalla arranca con lo que
// ya sabía y pide los datos en el mismo instante; la respuesta del servidor
// sigue llegando y solo se aplica si de verdad cambió (p. ej. el usuario tocó
// los filtros desde otra máquina), que es lo que mantiene vivo el sentido de
// guardar la preferencia en la nube.
const CLAVE_ESPEJO = 'fc:ui-prefs:v1';

const leerEspejo = () => {
  try {
    return JSON.parse(localStorage.getItem(CLAVE_ESPEJO) || '{}');
  } catch {
    return {};
  }
};

const escribirEspejo = (scope, value) => {
  try {
    const espejo = leerEspejo();
    espejo[scope] = value;
    localStorage.setItem(CLAVE_ESPEJO, JSON.stringify(espejo));
  } catch {
    // Sin espejo se sigue funcionando: sólo se pierde el arranque instantáneo.
  }
};

class UIPreferencesService {
  async getScope(scope) {
    const response = await authClient.get('ui-preferences/', {
      params: { scope },
    });
    const value = response?.data?.value;
    escribirEspejo(scope, value);
    return value;
  }

  /** Valor ya conocido de un scope, sin ir al servidor. `undefined` si no hay. */
  getScopeCacheado(scope) {
    return leerEspejo()[scope];
  }

  /**
   * Trae el scope resolviendo primero con lo que ya se sabía.
   *
   * @param {string} scope
   * @param {Function} alRevalidar  Recibe el valor del servidor SOLO si difiere
   *                                del que se entregó desde el espejo local.
   * @returns {{ value: any, desdeEspejo: boolean }}
   */
  async getScopeRapido(scope, alRevalidar) {
    const espejo = leerEspejo();
    const tieneEspejo = Object.prototype.hasOwnProperty.call(espejo, scope);

    if (!tieneEspejo) {
      const value = await this.getScope(scope);
      return { value, desdeEspejo: false };
    }

    const cacheado = espejo[scope];
    // Revalidación en segundo plano: no bloquea el pedido de datos.
    this.getScope(scope).then((fresco) => {
      if (typeof alRevalidar !== 'function') return;
      if (JSON.stringify(fresco ?? null) === JSON.stringify(cacheado ?? null)) return;
      alRevalidar(fresco);
    }).catch(() => {
      // El espejo ya cubrió la pantalla; un fallo acá no la rompe.
    });

    return { value: cacheado, desdeEspejo: true };
  }

  async setScope(scope, value) {
    const response = await authClient.patch('ui-preferences/', {
      scope,
      value,
    });
    escribirEspejo(scope, response?.data?.value);
    return response?.data?.value;
  }

  async clearScope(scope) {
    await authClient.patch('ui-preferences/', {
      scope,
      clear: true,
    });
    try {
      const espejo = leerEspejo();
      delete espejo[scope];
      localStorage.setItem(CLAVE_ESPEJO, JSON.stringify(espejo));
    } catch {
      // idem escribirEspejo
    }
  }
}

const uiPreferencesService = new UIPreferencesService();

export default uiPreferencesService;
